import React, { useState, useEffect, useMemo } from 'react';
import { confirmDialog } from '../../lib/confirm';
import axios from '../../../config/axios';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import Modal, { ModalLabel, ModalField, ModalError, modalInputStyle } from './Modal';
import PlantPickerInput from './PlantPickerInput';

/**
 * PlotModals — wszystkie modale CRUD dla działek i grządek.
 *
 * Eksportuje:
 *   PlotFormModal      - dodaj/edytuj działkę (POST/PUT /api/plots, FormData z opcjonalnym image)
 *   PlotDeleteModal    - confirm usunięcia działki (DELETE /api/plots/:id)
 *   BedFormModal       - dodaj/edytuj grządkę (POST/PUT /api/plots/:plotId/beds + /api/beds/:id)
 *   BedDuplicateModal  - sklonuj grządkę
 *   BedHarvestModal    - rejestracja zbioru (PUT /api/beds/:id/harvest, FormData)
 *   BedDeleteModal     - confirm usunięcia grządki (DELETE /api/beds/:id)
 *   BedPhotosModal     - galeria zdjęć grządki (GET/POST /api/beds/:bedId/photos, DELETE /api/photos/:id)
 */

// ────────────────────────────────────────────────────────────────────
// PlotFormModal
// ────────────────────────────────────────────────────────────────────
export function PlotFormModal({ open, onClose, onSaved, plot }) {
  const isEdit = !!plot;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(plot?.name || '');
      setDescription(plot?.description || '');
      setImageFile(null);
      setImagePreview(plot?.image_path ? `/${plot.image_path}` : null);
      setError('');
    }
  }, [open, plot]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Plik za duży (max 5 MB).');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Wybierz plik graficzny (JPG, PNG, WEBP).');
      return;
    }
    setError('');
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      setError('Nazwa działki jest wymagana.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('description', description.trim() || '');
      if (imageFile) formData.append('image', imageFile);

      let res;
      if (isEdit) {
        res = await axios.put(`/api/plots/${plot.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post('/api/plots', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edytuj działkę' : 'Nowa działka'}
      subtitle={isEdit
        ? 'Zmień nazwę, opis lub zdjęcie działki.'
        : 'Działka to fizyczne miejsce w Twoim ogrodzie — może mieć wiele grządek.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">Anuluj</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || !name.trim()}>
            {saving ? 'Zapisuję…' : (isEdit ? 'Zapisz zmiany' : 'Utwórz działkę')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <ModalError message={error} />
        <ModalField>
          <ModalLabel htmlFor="plot-name">Nazwa działki</ModalLabel>
          <input
            id="plot-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="np. Ogród warzywny, Rabata przed domem"
            maxLength={100}
            required
            autoFocus
            style={modalInputStyle}
          />
          <div style={{
            marginTop: 4, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{name.length} / 100</div>
        </ModalField>

        <ModalField>
          <ModalLabel htmlFor="plot-description">Opis (opcjonalny)</ModalLabel>
          <textarea
            id="plot-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Lokalizacja, gleba, nasłonecznienie…"
            maxLength={500}
            rows={3}
            style={{ ...modalInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
          />
          <div style={{
            marginTop: 4, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{description.length} / 500</div>
        </ModalField>

        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel>Zdjęcie działki (opcjonalne)</ModalLabel>
          {imagePreview ? (
            <div style={{
              position: 'relative',
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid var(--t-line)',
            }}>
              <img
                src={imagePreview}
                alt=""
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }}
              />
              <button
                type="button"
                onClick={() => { setImagePreview(null); setImageFile(null); }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'rgba(15,20,16,.85)', color: 'var(--t-text)',
                  border: 'none', cursor: 'pointer',
                  fontSize: 16, lineHeight: 1,
                }}
                aria-label="Usuń zdjęcie"
              >×</button>
            </div>
          ) : (
            <label htmlFor="plot-image" style={{
              display: 'block',
              padding: '24px 16px', borderRadius: 12,
              border: '1px dashed var(--t-line-2)',
              background: 'var(--t-bg-elev)',
              textAlign: 'center', cursor: 'pointer',
              fontSize: 13, color: 'var(--t-text-dim)',
            }}>
              + Kliknij żeby wybrać zdjęcie<br />
              <span style={{
                fontSize: 11, color: 'var(--t-text-mute)',
                fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
              }}>JPG, PNG, WEBP · max 5 MB</span>
            </label>
          )}
          <input
            id="plot-image"
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
        </ModalField>
      </form>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// PlotDeleteModal
// ────────────────────────────────────────────────────────────────────
export function PlotDeleteModal({ open, onClose, plot, bedsCount, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`/api/plots/${plot.id}`);
      onDeleted?.(plot.id);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć działki.');
    } finally {
      setDeleting(false);
    }
  };

  if (!plot) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Usunąć działkę?"
      subtitle="Akcja nieodwracalna."
      maxWidth={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>Anuluj</Button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: '10px 18px', borderRadius: 10,
              background: 'var(--t-rust)', color: 'var(--t-bg)',
              border: 'none', cursor: deleting ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
              opacity: deleting ? 0.6 : 1,
            }}
          >{deleting ? 'Usuwam…' : 'Tak, usuń działkę'}</button>
        </>
      }
    >
      <ModalError message={error} />
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        background: 'rgba(192, 106, 74, 0.08)',
        borderLeft: '3px solid var(--t-rust)',
        marginBottom: 14,
      }}>
        <Display size={16} style={{ display: 'block', marginBottom: 8 }}>{plot.name}</Display>
        <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55 }}>
          Razem z działką znikną:
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            <li>{bedsCount || 0} {bedsCount === 1 ? 'grządka' : bedsCount < 5 ? 'grządki' : 'grządek'}</li>
            <li>Cała historia oprysków, nawożenia i zbiorów dla tych grządek</li>
            <li>Zdjęcia każdej grządki w tej działce</li>
          </ul>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--t-text-mute)', lineHeight: 1.5 }}>
        Statystyki sezonu (np. łączny plon, liczba zadań) zostaną zachowane jako część historii konta.
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// BedFormModal (add + edit)
// ────────────────────────────────────────────────────────────────────
export function BedFormModal({ open, onClose, plotId, bed, existingRows = [], prefill, onSaved }) {
  const isEdit = !!bed;
  const [rowNumber, setRowNumber] = useState(1);
  const [plantName, setPlantName] = useState('');
  const [plantVariety, setPlantVariety] = useState('');
  const [plantedDate, setPlantedDate] = useState('');
  const [expectedHarvestDate, setExpectedHarvestDate] = useState('');
  const [autoHarvestDate, setAutoHarvestDate] = useState(null);
  const [autoDays, setAutoDays] = useState(null);
  const [note, setNote] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Suggest następny wolny rząd przy create
  const suggestedRow = useMemo(() => {
    if (existingRows.length === 0) return 1;
    return Math.max(...existingRows) + 1;
  }, [existingRows]);

  useEffect(() => {
    if (open) {
      setRowNumber(bed?.row_number || suggestedRow);
      // Prefill ma priorytet nad pustym stanem, ale bed ma priorytet nad prefill (edycja)
      setPlantName(bed?.plant_name || prefill?.plant_name || '');
      setPlantVariety(bed?.plant_variety || prefill?.plant_variety || '');
      setPlantedDate(bed?.planted_date || (prefill?.plant_name ? new Date().toISOString().split('T')[0] : ''));
      setExpectedHarvestDate(bed?.expected_harvest_date || '');
      setNote(bed?.note || '');
      setImageFile(null);
      setImagePreview(bed?.image_path ? `/${bed.image_path}` : null);
      setError('');
    }
  }, [open, bed, prefill, suggestedRow]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Plik za duży (max 5 MB).');
      return;
    }
    setError('');
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Sprawdza czy row_number nie jest zajęty (przy create lub edycji innego)
  const rowConflict = useMemo(() => {
    const n = parseInt(rowNumber, 10);
    if (!n || n < 1) return false;
    if (isEdit && bed.row_number === n) return false;
    return existingRows.includes(n);
  }, [rowNumber, existingRows, isEdit, bed]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!rowNumber || rowNumber < 1) {
      setError('Numer rzędu musi być liczbą większą od 0.');
      return;
    }
    if (rowConflict) {
      setError(`Rząd ${rowNumber} jest już zajęty w tej działce.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('row_number', rowNumber);
      if (plantName.trim()) formData.append('plant_name', plantName.trim());
      if (plantVariety.trim()) formData.append('plant_variety', plantVariety.trim());
      if (plantedDate) formData.append('planted_date', plantedDate);
      if (expectedHarvestDate) formData.append('expected_harvest_date', expectedHarvestDate);
      if (note.trim()) formData.append('note', note.trim());
      if (imageFile) formData.append('image', imageFile);

      let res;
      if (isEdit) {
        res = await axios.put(`/api/beds/${bed.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post(`/api/plots/${plotId}/beds`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onSaved?.(res.data?.bed || res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edytuj grządkę — rząd ${bed.row_number}` : 'Nowa grządka'}
      subtitle={isEdit
        ? 'Zmień roślinę, datę sadzenia, notatkę lub zdjęcie.'
        : 'Grządka to konkretne miejsce z jedną rośliną. Wybierz roślinę z bazy — automatycznie obliczy się termin zbioru.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={saving}>Anuluj</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !rowNumber || rowConflict}
          >{saving ? 'Zapisuję…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj grządkę')}</Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <ModalError message={error} />

        <ModalField>
          <ModalLabel htmlFor="bed-row">Numer rzędu</ModalLabel>
          <input
            id="bed-row"
            type="number"
            min={1}
            value={rowNumber}
            onChange={e => setRowNumber(parseInt(e.target.value, 10) || '')}
            style={{
              ...modalInputStyle,
              fontFamily: 'var(--t-mono)',
              borderColor: rowConflict ? 'var(--t-rust)' : 'var(--t-line-2)',
            }}
          />
          {rowConflict && (
            <div style={{
              marginTop: 6, fontSize: 12, color: 'var(--t-rust)',
            }}>⚠ Rząd {rowNumber} jest już zajęty w tej działce</div>
          )}
        </ModalField>

        <ModalField>
          <ModalLabel htmlFor="bed-plant">Roślina</ModalLabel>
          <PlantPickerInput
            id="bed-plant"
            value={plantName}
            onChange={(name, plant) => {
              setPlantName(name);
              if (plant && plant.display_name && !plantVariety) {
                // Można wyciągnąć z display_name jeśli jest w cudzysłowie
                const m = plant.display_name.match(/'([^']+)'/);
                if (m) setPlantVariety(m[1]);
              }
            }}
            plantedDate={plantedDate}
            onHarvestDateCalculated={(date, days) => {
              setAutoHarvestDate(date);
              setAutoDays(days);
            }}
          />
        </ModalField>

        <ModalField>
          <ModalLabel htmlFor="bed-variety">Odmiana (opcjonalna)</ModalLabel>
          <input
            id="bed-variety"
            type="text"
            value={plantVariety}
            onChange={e => setPlantVariety(e.target.value)}
            placeholder="np. Black Krim, Malinowy Olbrzym"
            maxLength={100}
            style={modalInputStyle}
          />
        </ModalField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <ModalField style={{ marginBottom: 0 }}>
            <ModalLabel htmlFor="bed-planted">Data sadzenia</ModalLabel>
            <input
              id="bed-planted"
              type="date"
              value={plantedDate}
              onChange={e => {
                setPlantedDate(e.target.value);
                if (e.target.value && autoHarvestDate && !expectedHarvestDate) {
                  // jeśli user nie nadpisał ręcznie, zaktualizujemy poniżej w useEffect picker'a
                }
              }}
              style={{ ...modalInputStyle, colorScheme: 'dark' }}
            />
          </ModalField>
          <ModalField style={{ marginBottom: 0 }}>
            <ModalLabel htmlFor="bed-harvest">Planowany zbiór</ModalLabel>
            <input
              id="bed-harvest"
              type="date"
              value={expectedHarvestDate}
              onChange={e => setExpectedHarvestDate(e.target.value)}
              placeholder={autoHarvestDate || ''}
              style={{ ...modalInputStyle, colorScheme: 'dark' }}
            />
          </ModalField>
        </div>

        {autoHarvestDate && expectedHarvestDate !== autoHarvestDate && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(168, 196, 158, 0.08)',
            borderLeft: '3px solid var(--t-sage)',
            fontSize: 12, color: 'var(--t-text-dim)',
            marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span>📅 Sugerowany termin zbioru: <strong style={{ color: 'var(--t-text)' }}>{autoHarvestDate}</strong> ({autoDays} dni od sadzenia)</span>
            <button
              type="button"
              onClick={() => setExpectedHarvestDate(autoHarvestDate)}
              style={{
                background: 'transparent', border: '1px solid var(--t-sage)',
                color: 'var(--t-sage)', cursor: 'pointer',
                padding: '4px 10px', borderRadius: 6,
                fontSize: 11, fontFamily: 'var(--t-mono)',
                letterSpacing: '.08em',
              }}
            >Użyj</button>
          </div>
        )}

        <ModalField>
          <ModalLabel htmlFor="bed-note">Notatka (opcjonalna)</ModalLabel>
          <textarea
            id="bed-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Obserwacje, plan na sezon, problemy…"
            maxLength={500}
            rows={3}
            style={{ ...modalInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
          />
        </ModalField>

        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel>Zdjęcie grządki (opcjonalne)</ModalLabel>
          {imagePreview ? (
            <div style={{
              position: 'relative', borderRadius: 12, overflow: 'hidden',
              border: '1px solid var(--t-line)',
            }}>
              <img
                src={imagePreview} alt=""
                style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }}
              />
              <button
                type="button"
                onClick={() => { setImagePreview(null); setImageFile(null); }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'rgba(15,20,16,.85)', color: 'var(--t-text)',
                  border: 'none', cursor: 'pointer',
                  fontSize: 16, lineHeight: 1,
                }}
                aria-label="Usuń zdjęcie"
              >×</button>
            </div>
          ) : (
            <label htmlFor="bed-image" style={{
              display: 'block', padding: '20px 16px', borderRadius: 12,
              border: '1px dashed var(--t-line-2)',
              background: 'var(--t-bg-elev)',
              textAlign: 'center', cursor: 'pointer',
              fontSize: 13, color: 'var(--t-text-dim)',
            }}>+ Wybierz zdjęcie</label>
          )}
          <input
            id="bed-image"
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
        </ModalField>
      </form>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// BedDuplicateModal
// ────────────────────────────────────────────────────────────────────
export function BedDuplicateModal({ open, onClose, plotId, bed, existingRows = [], onSaved }) {
  const [rowNumber, setRowNumber] = useState(1);
  const [plantedDate, setPlantedDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const suggestedRow = useMemo(() => {
    if (existingRows.length === 0) return 1;
    return Math.max(...existingRows) + 1;
  }, [existingRows]);

  useEffect(() => {
    if (open) {
      setRowNumber(suggestedRow);
      setPlantedDate(new Date().toISOString().split('T')[0]);
      setError('');
    }
  }, [open, suggestedRow]);

  const rowConflict = useMemo(() => {
    const n = parseInt(rowNumber, 10);
    return !!n && existingRows.includes(n);
  }, [rowNumber, existingRows]);

  const handleSubmit = async () => {
    if (rowConflict) {
      setError(`Rząd ${rowNumber} jest już zajęty.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('row_number', rowNumber);
      if (bed.plant_name) formData.append('plant_name', bed.plant_name);
      if (bed.plant_variety) formData.append('plant_variety', bed.plant_variety);
      formData.append('planted_date', plantedDate);
      if (bed.note) formData.append('note', bed.note);

      const res = await axios.post(`/api/plots/${plotId}/beds`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.(res.data?.bed || res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się sklonować grządki.');
    } finally {
      setSaving(false);
    }
  };

  if (!bed) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Powiel grządkę"
      subtitle="Klonuje roślinę, odmianę i notatkę. Zbiór i zdjęcia nie są kopiowane."
      maxWidth={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Anuluj</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || rowConflict}>
            {saving ? 'Klonuję…' : 'Powiel grządkę'}
          </Button>
        </>
      }
    >
      <ModalError message={error} />

      <div style={{
        padding: '14px 16px', borderRadius: 12,
        background: 'rgba(168, 196, 158, 0.06)',
        borderLeft: '3px solid var(--t-sage)',
        marginBottom: 18,
      }}>
        <Meta style={{ marginBottom: 4 }}>Klonujesz</Meta>
        <Display size={18} style={{ display: 'block' }}>
          {bed.plant_name || 'Pusta grządka'}
          {bed.plant_variety && (
            <span style={{ color: 'var(--t-text-dim)', fontSize: 14 }}> ({bed.plant_variety})</span>
          )}
        </Display>
        <div style={{ fontSize: 11, color: 'var(--t-text-mute)', marginTop: 4 }}>
          z rzędu {bed.row_number}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel htmlFor="dup-row">Nowy rząd</ModalLabel>
          <input
            id="dup-row"
            type="number"
            min={1}
            value={rowNumber}
            onChange={e => setRowNumber(parseInt(e.target.value, 10) || '')}
            style={{
              ...modalInputStyle,
              fontFamily: 'var(--t-mono)',
              borderColor: rowConflict ? 'var(--t-rust)' : 'var(--t-line-2)',
            }}
          />
          {rowConflict && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--t-rust)' }}>
              Rząd zajęty
            </div>
          )}
        </ModalField>
        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel htmlFor="dup-date">Data sadzenia</ModalLabel>
          <input
            id="dup-date"
            type="date"
            value={plantedDate}
            onChange={e => setPlantedDate(e.target.value)}
            style={{ ...modalInputStyle, colorScheme: 'dark' }}
          />
        </ModalField>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// BedHarvestModal
// ────────────────────────────────────────────────────────────────────
export function BedHarvestModal({ open, onClose, bed, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [harvestDate, setHarvestDate] = useState(today);
  const [yieldAmount, setYieldAmount] = useState('');
  const [yieldUnit, setYieldUnit] = useState('kg');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [clearBed, setClearBed] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setHarvestDate(today);
      setYieldAmount('');
      setYieldUnit('kg');
      setNotes('');
      setPhoto(null);
      setPhotoPreview(null);
      setClearBed(true);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Zdjęcie za duże (max 5 MB).');
      return;
    }
    setError('');
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    const hasYield = yieldAmount && parseFloat(yieldAmount) > 0;
    const hasNotes = notes.trim().length > 0;
    if (!hasYield && !photo && !hasNotes) {
      setError('Podaj przynajmniej: ilość plonu, zdjęcie albo opis zbioru.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('actual_harvest_date', harvestDate);
      if (hasYield) {
        formData.append('yield_amount', yieldAmount);
        formData.append('yield_unit', yieldUnit);
      }
      if (notes.trim()) formData.append('harvest_notes', notes.trim());
      if (photo) formData.append('harvest_photo', photo);
      formData.append('clearBed', clearBed ? 'true' : 'false');

      const res = await axios.put(`/api/beds/${bed.id}/harvest`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie udało się zapisać zbioru.');
    } finally {
      setSaving(false);
    }
  };

  if (!bed) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Zbiór: ${bed.plant_name || 'grządka'}`}
      subtitle="Zarejestruj plon — wpis trafi do statystyk sezonu i historii."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Anuluj</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Zapisuję…' : 'Zarejestruj zbiór'}
          </Button>
        </>
      }
    >
      <ModalError message={error} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel htmlFor="harv-date">Data zbioru</ModalLabel>
          <input
            id="harv-date" type="date"
            value={harvestDate}
            onChange={e => setHarvestDate(e.target.value)}
            max={today}
            required
            style={{ ...modalInputStyle, colorScheme: 'dark' }}
          />
        </ModalField>
        <ModalField style={{ marginBottom: 0 }}>
          <ModalLabel htmlFor="harv-yield">Ilość plonu</ModalLabel>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              id="harv-yield" type="number"
              step={0.1} min={0}
              value={yieldAmount}
              onChange={e => setYieldAmount(e.target.value)}
              placeholder="0.0"
              style={{ ...modalInputStyle, fontFamily: 'var(--t-mono)', textAlign: 'right' }}
            />
            <select
              aria-label="Jednostka plonu"
              value={yieldUnit}
              onChange={e => setYieldUnit(e.target.value)}
              style={{ ...modalInputStyle, width: 70, colorScheme: 'dark' }}
            >
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="szt">szt</option>
              <option value="l">l</option>
              <option value="pęczki">pęczki</option>
            </select>
          </div>
        </ModalField>
      </div>

      <ModalField>
        <ModalLabel htmlFor="harv-notes">Notatka o zbiorze (opcjonalna)</ModalLabel>
        <textarea
          id="harv-notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Smak, jakość, problemy, uwagi do następnego sezonu…"
          maxLength={500}
          rows={3}
          style={{ ...modalInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
        />
      </ModalField>

      <ModalField>
        <ModalLabel>Zdjęcie zbioru (opcjonalne)</ModalLabel>
        {photoPreview ? (
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
            <img src={photoPreview} alt=""
              style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
            <button
              type="button"
              onClick={() => { setPhoto(null); setPhotoPreview(null); }}
              style={{
                position: 'absolute', top: 8, right: 8,
                width: 30, height: 30, borderRadius: '50%',
                background: 'rgba(15,20,16,.85)', color: 'var(--t-text)',
                border: 'none', cursor: 'pointer',
                fontSize: 16, lineHeight: 1,
              }}
            >×</button>
          </div>
        ) : (
          <label htmlFor="harv-photo" style={{
            display: 'block', padding: '18px 16px', borderRadius: 12,
            border: '1px dashed var(--t-line-2)',
            background: 'var(--t-bg-elev)',
            textAlign: 'center', cursor: 'pointer',
            fontSize: 13, color: 'var(--t-text-dim)',
          }}>📷 Dodaj zdjęcie zbioru</label>
        )}
        <input
          id="harv-photo" type="file" accept="image/*"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </ModalField>

      <ModalField style={{ marginBottom: 0 }}>
        <ModalLabel>Co potem z grządką?</ModalLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { v: true,  l: 'Opróżnij grządkę', d: 'Roślina znika z planu, miejsce wolne na nowy siew. Zbiór i historia zachowane.' },
            { v: false, l: 'Zostaw jako historia', d: 'Grządka pozostaje z oznaczeniem zebrana — np. dla bylin z kolejnym sezonem.' },
          ].map(opt => {
            const on = clearBed === opt.v;
            return (
              <button
                key={String(opt.v)}
                type="button"
                onClick={() => setClearBed(opt.v)}
                style={{
                  textAlign: 'left',
                  padding: '12px 14px', borderRadius: 10,
                  background: on ? 'rgba(168, 196, 158, 0.08)' : 'var(--t-bg-elev)',
                  border: `1px solid ${on ? 'var(--t-sage)' : 'var(--t-line)'}`,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  border: `2px solid ${on ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
                  background: on ? 'var(--t-sage)' : 'transparent',
                  flexShrink: 0, marginTop: 2,
                  display: 'grid', placeItems: 'center',
                }}>
                  {on && <div style={{
                    width: 6, height: 6, borderRadius: '50%', background: 'var(--t-bg)',
                  }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'var(--t-text)', fontSize: 14, marginBottom: 2 }}>{opt.l}</div>
                  <div style={{ color: 'var(--t-text-dim)', fontSize: 12, lineHeight: 1.4 }}>{opt.d}</div>
                </div>
              </button>
            );
          })}
        </div>
      </ModalField>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// BedDeleteModal
// ────────────────────────────────────────────────────────────────────
export function BedDeleteModal({ open, onClose, bed, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`/api/beds/${bed.id}`);
      onDeleted?.(bed.id);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć grządki.');
    } finally {
      setDeleting(false);
    }
  };

  if (!bed) return null;

  const daysGrowing = bed.planted_date
    ? Math.floor((Date.now() - new Date(bed.planted_date).getTime()) / 86400000)
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Usunąć grządkę?"
      subtitle="Akcja nieodwracalna."
      maxWidth={440}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>Anuluj</Button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: '10px 18px', borderRadius: 10,
              background: 'var(--t-rust)', color: 'var(--t-bg)',
              border: 'none', cursor: deleting ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
              opacity: deleting ? 0.6 : 1,
            }}
          >{deleting ? 'Usuwam…' : 'Tak, usuń'}</button>
        </>
      }
    >
      <ModalError message={error} />
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        background: 'rgba(192, 106, 74, 0.08)',
        borderLeft: '3px solid var(--t-rust)',
        marginBottom: 14,
      }}>
        <Display size={18} style={{ display: 'block', marginBottom: 6 }}>
          {bed.plant_name || 'Pusta grządka'}
          {bed.plant_variety && (
            <span style={{ fontSize: 14, color: 'var(--t-text-dim)' }}> ({bed.plant_variety})</span>
          )}
        </Display>
        <div style={{
          fontSize: 12, color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          rząd {bed.row_number}
          {daysGrowing != null && daysGrowing >= 0 && ` · rośnie ${daysGrowing} dni`}
        </div>
      </div>

      <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55 }}>
        Razem z grządką znikną:
        <ul style={{ margin: '8px 0 14px', paddingLeft: 18 }}>
          <li>Historia oprysków i nawożenia</li>
          <li>Plan zbiorów i zarejestrowane plony</li>
          <li>Zdjęcia bezpośrednio przypisane do grządki</li>
        </ul>
        <Chip tone="sage">✓ Zdjęcia w galerii zostają</Chip>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────
// BedPhotosModal — galeria zdjęć grządki
// ────────────────────────────────────────────────────────────────────
export function BedPhotosModal({ open, onClose, bed }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [takenDate, setTakenDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');

  const loadPhotos = async () => {
    if (!bed) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/beds/${bed.id}/photos`);
      setPhotos(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się pobrać zdjęć.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && bed) {
      loadPhotos();
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bed?.id]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Zdjęcie za duże (max 5 MB).');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!photoFile) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      if (caption.trim()) formData.append('caption', caption.trim());
      if (takenDate) formData.append('taken_date', takenDate);
      await axios.post(`/api/beds/${bed.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoFile(null);
      setPhotoPreview(null);
      setCaption('');
      loadPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się przesłać.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!await confirmDialog('Usunąć to zdjęcie?')) return;
    try {
      await axios.delete(`/api/photos/${photoId}`);
      loadPhotos();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć.');
    }
  };

  if (!bed) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Zdjęcia: ${bed.plant_name || 'grządka'}`}
      subtitle={`Galeria zdjęć z konkretnej grządki (rząd ${bed.row_number}).`}
      maxWidth={760}
    >
      <ModalError message={error} />

      {/* Upload form */}
      <div style={{
        padding: '16px 18px', borderRadius: 12,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        marginBottom: 24,
      }}>
        <Meta style={{ marginBottom: 10 }}>Dodaj nowe zdjęcie</Meta>
        {photoPreview ? (
          <div style={{
            position: 'relative', borderRadius: 10, overflow: 'hidden',
            marginBottom: 14, maxHeight: 180,
          }}>
            <img src={photoPreview} alt=""
              style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
            <button
              type="button"
              onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
              style={{
                position: 'absolute', top: 8, right: 8,
                width: 30, height: 30, borderRadius: '50%',
                background: 'rgba(15,20,16,.85)', color: 'var(--t-text)',
                border: 'none', cursor: 'pointer',
                fontSize: 16, lineHeight: 1,
              }}
            >×</button>
          </div>
        ) : (
          <label htmlFor="bed-photo-up" style={{
            display: 'block', padding: '20px 16px', borderRadius: 10,
            border: '1px dashed var(--t-line-2)',
            background: 'var(--t-bg)',
            textAlign: 'center', cursor: 'pointer',
            fontSize: 13, color: 'var(--t-text-dim)',
            marginBottom: 14,
          }}>+ Wybierz zdjęcie</label>
        )}
        <input
          id="bed-photo-up" type="file" accept="image/*"
          onChange={handleFile}
          style={{ display: 'none' }}
        />

        {photoFile && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
              <input
                type="text"
                aria-label="Opis zdjęcia"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="Opis (opcjonalny)"
                maxLength={200}
                style={modalInputStyle}
              />
              <input
                type="date"
                aria-label="Data zdjęcia"
                value={takenDate}
                onChange={e => setTakenDate(e.target.value)}
                style={{ ...modalInputStyle, colorScheme: 'dark' }}
              />
              <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? '…' : 'Dodaj'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Lista zdjęć */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--t-text-mute)' }}>Ładowanie…</div>
      ) : photos.length === 0 ? (
        <div style={{
          padding: '40px 20px', textAlign: 'center',
          color: 'var(--t-text-mute)', fontSize: 13, fontStyle: 'italic',
        }}>↳ Jeszcze brak zdjęć z tej grządki. Dodaj pierwsze powyżej.</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 10,
        }}>
          {photos.map(p => {
            const src = p.photo_path?.startsWith('/') ? p.photo_path : `/${p.photo_path}`;
            return (
              <div key={p.id} style={{
                position: 'relative', aspectRatio: '1 / 1',
                borderRadius: 10, overflow: 'hidden',
                background: 'var(--t-bg-elev)',
                border: '1px solid var(--t-line)',
              }}>
                <img src={src} alt={p.caption || ''}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <button
                  type="button"
                  onClick={() => handleDelete(p.id)}
                  style={{
                    position: 'absolute', top: 6, right: 6,
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'rgba(15,20,16,.85)', color: 'var(--t-rust)',
                    border: 'none', cursor: 'pointer',
                    fontSize: 14, lineHeight: 1,
                  }}
                  aria-label="Usuń zdjęcie"
                >×</button>
                {(p.caption || p.taken_date) && (
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '6px 8px',
                    background: 'linear-gradient(transparent, rgba(15,20,16,.9))',
                    fontSize: 10, color: 'var(--t-text)',
                    fontFamily: 'var(--t-mono)', letterSpacing: '.04em',
                    lineHeight: 1.3,
                  }}>
                    {p.caption && (
                      <div style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{p.caption}</div>
                    )}
                    {p.taken_date && (
                      <div style={{
                        opacity: 0.6, marginTop: 2,
                      }}>{new Date(p.taken_date).toLocaleDateString('pl-PL')}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
